"use client";

// src/components/CategorySection.tsx
import Link from 'next/link';
import Image from 'next/image';
import CategorySkeleton from './CategorySkeleton';
import SectionHeading from './SectionHeading';

import { Category } from '../types';

type CategorySectionProps = {
  categories: Category[];
  loading: boolean;
  isFilter?: boolean;
  activeCategory?: string | 'all';
  onCategoryClick?: (name: string | 'all') => void;
  title?: string;
  subtitle?: string;
};

const CategoryItemDisplay = ({
  name,
  image_url,
  isActive
}: {
  name: string;
  image_url?: string;
  isActive: boolean
}) => {
  const isAllButton = !image_url;
  return (
    <div className="group flex flex-col items-center cursor-pointer">
      <div className={`
        relative h-20 w-20 rounded-2xl border p-1 transition-all duration-200 md:h-24 md:w-24
        ${isActive ? 'border-primary bg-orange-50' : 'border-gray-100 bg-white group-hover:border-primary/30 group-hover:bg-orange-50/60'}
      `}>
        <div className={`relative h-full w-full overflow-hidden rounded-xl ${isAllButton ? 'flex items-center justify-center bg-accent' : ''}`}>
          {!isAllButton && image_url && (
            <Image
              src={image_url}
              alt={name ? `${name} - AstroJi Spiritual Product Category` : 'AstroJi Spiritual Category'}
              fill
              sizes="(max-width: 768px) 80px, 96px"
              unoptimized={process.env.NODE_ENV === 'development'}
              className="object-cover object-center"
            />
          )}
          {isAllButton && <span className="font-serif font-bold text-gold text-lg text-center relative z-10">View All</span>}
        </div>
      </div>
      <p className={`
        mt-2 max-w-[7rem] text-center text-sm font-semibold leading-tight transition-colors duration-200 capitalize
        ${isActive ? 'text-primary' : 'text-accent group-hover:text-primary'}
      `}>
        {name}
      </p>
    </div>
  );
};

const CategorySection = ({
  categories,
  loading,
  isFilter = false,
  activeCategory,
  onCategoryClick,
  title
}: CategorySectionProps) => {
  return (
    <section className={`${isFilter ? 'pb-8 pt-0' : 'ui-section bg-white'}`}>
      <div className="ui-container">
        {title && (
          <SectionHeading title={title} />
        )}

        <div className={`flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain pb-3 md:gap-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${isFilter ? '' : 'md:[&>*:first-child]:ml-auto md:[&>*:last-child]:mr-auto'}`}>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="shrink-0 snap-start">
                <CategorySkeleton />
              </div>
            ))
          ) : (
            <>
              {isFilter && onCategoryClick && (
                <div className="shrink-0 snap-start cursor-pointer" onClick={() => onCategoryClick('all')}>
                  <CategoryItemDisplay
                    name="All"
                    isActive={activeCategory === 'all' || !activeCategory}
                  />
                </div>
              )}
              {categories.map((category) => (
                isFilter && onCategoryClick ? (
                  <div key={category.id} className="shrink-0 snap-start cursor-pointer" onClick={() => onCategoryClick(category.name)}>
                    <CategoryItemDisplay
                      name={category.name}
                      image_url={category.image_url}
                      isActive={typeof activeCategory === 'string' && activeCategory.toLowerCase() === category.name.toLowerCase()}
                    />
                  </div>
                ) : (
                  <Link prefetch={false} href={`/products?byTag=${category.tag}`} key={category.id} className="shrink-0 snap-start">
                    <CategoryItemDisplay
                      name={category.name}
                      image_url={category.image_url}
                      isActive={false}
                    />
                  </Link>
                )
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CategorySection;
